import { Panel } from "../../../components/panels/Panel";
import { useGetData } from "../../../services/api/api-tools";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  InboxIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";
import { getInfluxdbData } from "../../../services/api/influxdb-tools";
import { formatSecondsToFullDateTime, formatSecondsToHMS } from "../../../helpers/date/date-helper";
import Badge from "../../../components/badges/Badge";
import { getLISIQuery } from "../../../services/dashboard/lisi-dashboard-service";

export const LisiDashboardOF = ({ selectedRange }) => {

  const handleRangeSelection = (start, end) => {
    setSelectedPatternExecution(null);
    updateData(Math.floor(start / 1000) - 3600 * 24, Math.floor(end / 1000) + 3600 * 24);
  };
  useEffect(() => {
    handleRangeSelection(selectedRange.start, selectedRange.end)
  }, [selectedRange]);

  /* Racks list management */
  const [selectedPatternExecution, setSelectedPatternExecution] = useState(null);
  const [piecesData, setPiecesData] = useState([]);
  const [metrics, setMetrics] = useState({});

  const updateData = async (start, end) => {
    const newPiecesData = await getInfluxdbData(
      'engine',
      'events',
      [],
      ['data_', 'pattern_execution_id', 'subtype'],
      start,
      end,
      '1ms',
      null,
      null,
      [],
      null,
      null,
      false,
      'asc',
    );
    setPiecesData(newPiecesData);
  };

  const computeMetrics = async (patternExecution) => {
    const metricsNames = ['hit_pieces_number', 'exceeded_oven_time_number', 'mean_oven_time', 'mean_transfer_time'];

    const metricsEntries = await Promise.all(
      metricsNames.map(async (metricName) => {
        const result = await getInfluxdbData(
          null, null, [], [], null, null, null, null, null, null, null,
          getLISIQuery(metricName, patternExecution.start_date, patternExecution.stop_date),
        );
        return [metricName, result?.[0]?._value ?? null];
      })
    );

    setMetrics(Object.fromEntries(metricsEntries));
  };

  const handlePatternExecutionSelection = (patternExecution) => {
    setSelectedPatternExecution(patternExecution);
    setMetrics({});
    computeMetrics(patternExecution);
  };

  /* Process errors check */
  const { data: patternExecutions, isLoading: patternExecutionsAreLoading } = useGetData('pattern_executions', `pattern_executions`, {}, !!localStorage.getItem('token'));
  const patternExecutionsInRange = useMemo(() => patternExecutions?.filter((execution) =>
    execution.start_date >= Math.floor(selectedRange.start.getTime() / 1000) &&
    execution.start_date <= Math.floor(selectedRange.end.getTime() / 1000)
  ), [selectedRange]) ?? [];

  const _isPieceOk = useCallback((pieceLog, patternExecution) => {
    const patternData = patternExecution?.pattern?.pattern_data.reduce((acc, { name, value }) => ({ ...acc, [name]: value }), {});
    const isOvenTimeTooShort = pieceLog?.data_oven_time < parseInt(patternData?.oven_time);
    const isOvenTimeTooLong = parseInt(patternData?.oven_time) + parseInt(patternData?.oven_tolerance) < pieceLog?.data_oven_time;
    const isTransferTimeTooLong = parseInt(patternData?.transfer_time) + parseInt(patternData?.transfer_tolerance) < pieceLog?.data_transfer_time;
    return !isOvenTimeTooShort && !isOvenTimeTooLong && !isTransferTimeTooLong;
  });

  return (
    <div className="h-full flex-1 flex gap-4 min-h-0">

      {/* Left part */}
      <div className="h-full flex flex-col gap-4 w-1/3">
        <Panel title="LISTE DES OF" height="h-1/2" rounded>
          <div className="flex flex-col gap-4">
            {
              patternExecutionsInRange.map((patternExecution, patternExecutionIndex) => {
                const patternExecutionTheoricalQuantity = patternExecution.data?.quantity ?? 0;

                const patternExecutionPieces = piecesData.filter((piece) => piece.data_pattern_execution_id === patternExecution.id);
                const piecesOk = patternExecutionPieces.filter((piece) => _isPieceOk(piece, patternExecution));

                return (
                  <div className={`bg-perception-black-700 px-4 py-4 rounded-md relative overflow-hidden cursor-pointer border border-2
                        ${selectedPatternExecution?.id === patternExecution.id ? 'border-perception-blue' : 'border-transparent'}`}
                       onClick={() => handlePatternExecutionSelection(patternExecution)} key={patternExecution.id}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <Badge text={formatSecondsToFullDateTime(patternExecution.start_date, false)} />
                        <div>
                          {patternExecution?.data?.of && `#${patternExecution.data.of} - `}
                          {patternExecution.pattern.name}
                        </div>
                        {/*<Badge text={`${patternExecutionTheoricalQuantity} pièces`} />*/}
                        {!patternExecution?.finished && <Badge text="En cours" type="blue" />}
                      </div>
                      {/* Complete tag */}
                      {
                        piecesOk.length >= patternExecutionTheoricalQuantity ?
                          <Badge text={`Conforme (${piecesOk.length}/${patternExecutionTheoricalQuantity})`} type="green" /> :
                          <Badge text={`Non conforme (${piecesOk.length}/${patternExecutionTheoricalQuantity})`} type='yellow' />
                      }
                    </div>
                  </div>
                )
              })
            }
            {
              patternExecutionsInRange.length === 0 &&
              <div className="flex flex-col gap-4 h-full items-center justify-center text-center">
                <InboxIcon className="size-32 text-perception-gray-700"/>
                <div className="text-perception-gray-700 text-2xl">Pour démarrer, sélectionner une période</div>
              </div>
            }
          </div>
        </Panel>

        <Panel title="DONNEES SUR L'OF" height="h-1/2" rounded>
          {selectedPatternExecution ?
            <div className="flex flex-col gap-4 h-full items-center justify-center text-center">
              <div className="flex gap-4 w-full items-center text-center text-xl">
                <div className="w-1/2 flex flex-col gap-2 bg-perception-black-700 rounded-md p-4">
                  <div className="text-5xl text-perception-blue">{metrics?.hit_pieces_number ?? '-'}</div>
                  <div>Pièces frappées</div>
                </div>
                <div className="w-1/2 flex flex-col gap-2 bg-perception-black-700 rounded-md p-4">
                  <div className="text-5xl text-perception-blue">{metrics?.exceeded_oven_time_number ?? '-'}</div>
                  <div>Temps de chauffe dépassés</div>
                </div>
              </div>
              <div className="flex gap-4 w-full items-center text-center text-xl">
                <div className="w-1/2 flex flex-col gap-2 bg-perception-black-700 rounded-md p-4">
                  <div className="text-5xl text-perception-blue">{formatSecondsToHMS(metrics?.mean_oven_time, 'short') || '-'}</div>
                  <div>Temps chauffe moyen</div>
                </div>
                <div className="w-1/2 flex flex-col gap-2 bg-perception-black-700 rounded-md p-4">
                  <div className="text-5xl text-perception-blue">{formatSecondsToHMS(metrics?.mean_transfer_time) || '-'}</div>
                  <div>Temps transfert moyen</div>
                </div>
              </div>
            </div>
            :
            <div className="flex flex-col gap-4 h-full items-center justify-center text-center">
              <InboxIcon className="size-32 text-perception-gray-700"/>
              <div className="text-perception-gray-700 text-2xl">Aucun OF sélectionné</div>
            </div>
          }
        </Panel>
      </div>

      {/* Right part */}
      <Panel title="DETAILS DE L'OF" width="w-2/3" rounded>
        {selectedPatternExecution ?
          <div className="flex flex-col gap-2">
            {piecesData.filter((piece, pieceIndex) => piece.data_pattern_execution_id === selectedPatternExecution.id).map((piece, pieceIndex) => {
              const tsMs = new Date(piece?.time).getTime() / 1000;

              let patternExecutionName = 'Pièce hors gamme';

              let isOvenTimeTooShort = false, isOvenTimeTooLong = false, isTransferTimeTooLong = false;
              if (selectedPatternExecution !== null) {
                const patternData = selectedPatternExecution?.pattern?.pattern_data.reduce((acc, { name, value }) => ({ ...acc, [name]: value }), {});
                patternExecutionName = selectedPatternExecution.pattern.name;

                isOvenTimeTooShort = piece?.data_oven_time < parseInt(patternData?.oven_time)
                isOvenTimeTooLong = parseInt(patternData?.oven_time) + parseInt(patternData?.oven_tolerance) < piece?.data_oven_time;
                isTransferTimeTooLong = parseInt(patternData?.transfer_time) + parseInt(patternData?.transfer_tolerance) < piece?.data_transfer_time;
              }
              const pieceIsOk = !isOvenTimeTooShort && !isOvenTimeTooLong && !isTransferTimeTooLong;

              const textColor = !pieceIsOk ? 'text-perception-warn-500 font-medium' : '';

              return (
                <div className="flex-1 bg-perception-black-800 px-4 pr-1 py-1 rounded-md flex justify-between items-center" key={pieceIndex}>
                  <div className="flex items-center gap-4">
                    <InformationCircleIcon className={`size-4 min-w-4 ${textColor}`}/>
                    <div className={`text-sm ${textColor} flex gap-2`}>
                      Pièce frappée
                      {piece?.data_piece_id !== null && <Badge text={`#${piece?.data_piece_id}`} />}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="grid grid-cols-4 gap-2">
                      <Badge text={patternExecutionName} />
                      <Badge
                        text={`Chauffe : ${formatSecondsToHMS(piece?.data_oven_time) || '-'}`}
                        type={(isOvenTimeTooShort || isOvenTimeTooLong) ? 'yellow' : selectedPatternExecution !== null ? 'green' : 'gray'}
                      />
                      <Badge
                        text={`Four <> Frappe : ${formatSecondsToHMS(piece?.data_transfer_time) || '-'}`}
                        type={isTransferTimeTooLong ? 'yellow' : selectedPatternExecution !== null ? 'green' : 'gray'}
                      />
                      <Badge
                        text={`Frappe <> Découpe : ${formatSecondsToHMS(piece?.data_transfer_hit_cut_time) || '-'}`}
                        type={selectedPatternExecution !== null ? 'green' : 'gray'}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          :
          <div className="flex flex-col gap-4 h-full items-center justify-center text-center">
            <InboxIcon className="size-32 text-perception-gray-700"/>
            <div className="text-perception-gray-700 text-2xl">Aucun OF sélectionné</div>
          </div>
        }
      </Panel>
    </div>
  )
};